import { uploadFiles } from '../../api/services';
import { normalizeIban } from '../../utils';
import type { UserProfileData } from '../../hooks';
import type { Permission, StrapiFile } from '../../types';

export type BrandGraphicsFormData = {
  coverImage?: StrapiFile;
  gallery?: StrapiFile[];
  logo?: StrapiFile;
  coverImageFile?: File | null;
  galleryFiles?: File[];
  logoFile?: File | null;
};

export type MarketingConsents = {
  marketing_email_consent: boolean;
  marketing_sms_consent: boolean;
};

type ProfileAddressFormState = {
  address: string;
  city: string;
  email: string;
  lat: string;
  lng: string;
  nip: string;
  phone: string;
  regon: string;
};

export type CompanyProfileFormState = BrandGraphicsFormData & ProfileAddressFormState & {
  company_name: string;
  description: string;
  website: string;
};

export type PilotProfileFormState = BrandGraphicsFormData & ProfileAddressFormState & {
  ibanAccount: string;
  name: string;
  permissions: Permission[];
  surname: string;
};

export type ProfileFormState = CompanyProfileFormState | PilotProfileFormState;

export function createProfileFormState(profile: UserProfileData): ProfileFormState {
  if (profile.type === 'COMPANY') {
    const data = profile.fullData;
    const info = data.company_information;

    return {
      company_name: info.company_data?.company_name || '',
      description: info.company_data?.description || '',
      nip: info.tax_information?.tax_identification_number || '',
      regon: info.tax_information?.regon || '',
      email: info.contact_details?.email || '',
      phone: info.contact_details?.phone_number || '',
      website: info.contact_details?.website_url || '',
      city: info.contact_details?.location?.city || '',
      address: info.contact_details?.location?.address || '',
      lat: info.contact_details?.location?.lat?.toString() || '',
      lng: info.contact_details?.location?.lng?.toString() || '',
      coverImage: info.brand_graphics?.coverImage,
      gallery: info.brand_graphics?.gallery || [],
      logo: info.brand_graphics?.logo,
      coverImageFile: null,
      galleryFiles: [],
      logoFile: null,
    };
  }

  const data = profile.fullData;
  const info = data.company_information;

  return {
    name: data.personal_data?.name || '',
    surname: data.personal_data?.surname || '',
    nip: info.tax_information?.tax_identification_number || '',
    regon: info.tax_information?.regon || '',
    email: info.contact_details?.email || '',
    phone: info.contact_details?.phone_number || '',
    ibanAccount: data.ibanAccount || '',
    city: info.contact_details?.location?.city || '',
    address: info.contact_details?.location?.address || '',
    lat: info.contact_details?.location?.lat?.toString() || '',
    lng: info.contact_details?.location?.lng?.toString() || '',
    coverImage: info.brand_graphics?.coverImage,
    gallery: info.brand_graphics?.gallery || [],
    logo: info.brand_graphics?.logo,
    coverImageFile: null,
    galleryFiles: [],
    logoFile: null,
    permissions: data.permissions || [],
  };
}

export function isPilotProfileFormState(formData: ProfileFormState): formData is PilotProfileFormState {
  return 'permissions' in formData;
}

export function isCompanyProfileFormState(formData: ProfileFormState): formData is CompanyProfileFormState {
  return 'company_name' in formData;
}

async function buildBrandGraphicsPayload(formData: BrandGraphicsFormData) {
  const [uploadedLogo, uploadedCover, uploadedGallery] = await Promise.all([
    formData.logoFile ? uploadFiles([formData.logoFile]) : Promise.resolve([]),
    formData.coverImageFile ? uploadFiles([formData.coverImageFile]) : Promise.resolve([]),
    formData.galleryFiles?.length ? uploadFiles(formData.galleryFiles) : Promise.resolve([]),
  ]);

  const logo = uploadedLogo[0]?.id ?? formData.logo?.id;
  const coverImage = uploadedCover[0]?.id ?? formData.coverImage?.id;
  const gallery = [
    ...(formData.gallery ?? []).map((image) => image.id),
    ...uploadedGallery.map((image) => image.id),
  ];

  return {
    coverImage: coverImage ?? null,
    gallery,
    logo: logo ?? null,
  };
}

export async function buildProfileUpdatePayload(profile: UserProfileData, formData: ProfileFormState) {
  const brandGraphicsPayload = await buildBrandGraphicsPayload(formData);
  const brandGraphicsUpdate = Object.keys(brandGraphicsPayload).length
    ? { brand_graphics: brandGraphicsPayload }
    : {};

  if (profile.type === 'COMPANY' && isCompanyProfileFormState(formData)) {
    return {
      company_information: {
        ...brandGraphicsUpdate,
        company_data: {
          company_name: formData.company_name.trim(),
          description: formData.description.trim(),
        },
        tax_information: {
          tax_identification_number: formData.nip.trim(),
          regon: formData.regon.trim(),
        },
        contact_details: {
          email: formData.email.trim(),
          phone_number: formData.phone.trim(),
          website_url: formData.website.trim(),
          location: {
            city: formData.city.trim(),
            address: formData.address.trim(),
            lat: formData.lat ? parseFloat(formData.lat) : undefined,
            lng: formData.lng ? parseFloat(formData.lng) : undefined,
          },
        },
      },
    };
  }

  if (profile.type === 'PILOT' && isPilotProfileFormState(formData)) {
    return {
      personal_data: {
        name: formData.name.trim(),
        surname: formData.surname.trim(),
      },
      ibanAccount: normalizeIban(formData.ibanAccount),
      permissions: formData.permissions,
      company_information: {
        ...brandGraphicsUpdate,
        tax_information: {
          tax_identification_number: formData.nip.trim(),
          regon: formData.regon.trim(),
        },
        contact_details: {
          email: formData.email.trim(),
          phone_number: formData.phone.trim(),
          location: {
            city: formData.city.trim(),
            address: formData.address.trim(),
            lat: formData.lat ? parseFloat(formData.lat) : undefined,
            lng: formData.lng ? parseFloat(formData.lng) : undefined,
          },
        },
      },
    };
  }

  throw new Error('Nie udało się przygotować danych profilu do zapisu.');
}
